<template>
  <UkdIngredientDetailsModal
    v-if="detailsModalVisible"
    :agencyId="modalProduct.AgencyId"
    @closeModal="detailsModalVisible = false"
  />
  <CopyIngredientsModal
    v-if="copyIngredientsModalVisible"
    :plants="plants"
    :ingredientIds="selectedIngredientIds"
    @closeModal="onCopyModalClosed"
  />
  <IngredientApplicationModal
    v-if="ingredientApplicationModalVisible"
    :plants="plants"
    :ukdMode="true"
    @closeModal="ingredientApplicationModalVisible = false"
  />
  <div class="ukd-ingredients-table" data-cy="ukd-ingredients-table">
    <div class="grid-control__container" data-cy="header-container">
      <div v-if="canModifyCarts" class="ukd-ingredients-table__actions">
        <Button
          type="button"
          theme-color="primary"
          :disabled="!selectedIngredientIds.length"
          @click="onCopyClicked"
          data-cy="copy-ingredients-button"
          >Add Selected Ingredients</Button
        >
        <span>OR</span>
        <Button
          type="button"
          theme-color="success"
          @click="onAddClicked"
          data-cy="add-ingredients-button"
          >Can't find an ingredient? Enter a UKD ID.</Button
        >
      </div>
      <div class="pull-right">
        <TableSearch
          class="ukd-ingredients-search"
          :value="searchInput"
          placeholder="Search by Name or Company or Brand or UKD ID"
          @searchSubmitted="onSearchSubmitted"
        />
        <GridActionsMenu
          @actionClicked="onActionClicked"
          data-cy="grid-actions"
        />
      </div>
    </div>
    <UkdIngredientsGrid
      :products="products"
      :productsLoading="productsLoading"
      :pageSize="productsTake"
      :skip="productsSkip"
      :totalProducts="totalProducts"
      :filter="productsFilter"
      :canModifyCarts="canModifyCarts"
      :sort="sortDescriptors"
      ref="gridRef"
      :getPropValues="getProductPropValuesAsync"
      @productDetailsClicked="onDetailsClicked"
      @sortChanged="onSortChanged"
      @productsChanged="onProductsChanged"
      @selectionChanged="onSelectionChanged"
      @columnreorder="updateColumnMenuIcons"
      @columnssubmit="updateColumnMenuIcons"
    />
    <ColumnMenuIconHandler :filter="productsFilter" ref="menuIconHandlerRef" />
  </div>
</template>

<script setup lang="ts">
import { api } from "@/api";
import { useMainStore } from "@/stores/useMainStore";
import { computed, onBeforeMount, ref, watchEffect } from "vue";
import UkdIngredientsGrid from "./UkdIngredientsGrid.vue";
import type {
  CompositeFilterDescriptor,
  FilterDescriptor,
  SortDescriptor,
} from "@progress/kendo-data-query";
import TableSearch from "@/shared/ui/inputs/TableSearch.vue";

import type { GetMatchingProductsOnUkdOnSingleSearchFieldWithFilteringRequest } from "@/api/ukdProducts";
import { createCacheKey } from "@/shared/helpers/cacheHelper";
import { CacheService } from "@/shared/services/CacheService";
import type {
  ItemChangedOptions,
  ItemSortChangedOptions,
} from "@/Domain/ItemChangedOptions";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import {
  FilterValuesTake,
  getJsonFilterString,
} from "@/shared/helpers/filtersHelper";
import ColumnMenuIconHandler from "@/shared/ui/filters/ColumnMenuIconHandler.vue";
import { GridOptionsService } from "@/shared/services/GridOptionsService";
import { Button } from "@progress/kendo-vue-buttons";
import type { IdAndName } from "@/Domain/IdAndName";
import type { UkdProductWithSelection } from "@/api/scheduleA";
import CopyIngredientsModal from "./CopyIngredientsModal.vue";
import IngredientApplicationModal from "./IngredientApplicationModal.vue";
import UkdIngredientDetailsModal from "./UkdIngredientDetailsModal.vue";
import { GridMenuActionType } from "@/Domain/GridMenuActionType";
import GridActionsMenu from "@/shared/ui/inputs/GridActionsMenu.vue";
import type { PlantWithSelection } from "@/api/scheduleB";
import type { GridColumnPropsExtended } from "@/Domain/GridColumnPropsExtended";

const main = useMainStore();

const props = defineProps<{
  plants: IdAndName[];
  canModifyCarts: boolean;
}>();

const searchInput = ref<string>(main.UkdProductFilterCriteria);
const products = ref<UkdProductWithSelection[]>([]);
const selectedIngredientIds = ref<string[]>([]);
const totalProducts = ref<number>(0);
const productsLoading = ref<boolean>(false);

const detailsModalVisible = ref<boolean>(false);
const copyIngredientsModalVisible = ref<boolean>(false);
const ingredientApplicationModalVisible = ref<boolean>(false);

const modalProduct = ref<UkdProductWithSelection>(
  {} as UkdProductWithSelection
);

const productsFilter = ref<CompositeFilterDescriptor>({
  logic: "and",
  filters: [{} as FilterDescriptor],
});

const sortDescriptors = computed(() => {
  return (
    sortField.value
      ? [
          {
            field: sortField.value,
            dir: sortAscending.value ? "asc" : "desc",
          },
        ]
      : []
  ) as SortDescriptor[];
});

const defaultTake = 25;

const sortField = ref<string>("");
const sortAscending = ref<boolean>(false);
const productsTake = ref<number>(defaultTake);
const productsSkip = ref<number>(0);
const clearGrid = ref<boolean>(false);

const gridRef = ref<InstanceType<typeof UkdIngredientsGrid> | null>(null);
const menuIconHandlerRef = ref<InstanceType<
  typeof ColumnMenuIconHandler
> | null>(null);

const tableName = "UkdIngredients";

const getMatchingProductsAsync = async function (
  searchString: string,
  skip: number,
  take: number,
  filter: CompositeFilterDescriptor | null = null,
  valuesForFieldOnly: string = ""
) {
  const requestParams = {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    searchString: searchString,
    caller: main.userLogin,
    filters: getJsonFilterString(filter),
    skip: skip,
    take: take,
    includeConfidentials: false,
    sortField: sortField.value,
    ascending: sortAscending.value,
    valuesForFieldOnly: valuesForFieldOnly,
  } as GetMatchingProductsOnUkdOnSingleSearchFieldWithFilteringRequest;

  const endpoint = "GetMatchingProductsOnUkdOnSingleSearchFieldWithFiltering";

  if (!valuesForFieldOnly) return api.get(endpoint, requestParams);

  requestParams.take = FilterValuesTake;
  requestParams.skip = 0;

  return CacheService.getAsync(createCacheKey(requestParams, endpoint), () =>
    api.get(endpoint, requestParams)
  );
};

const clearFilters = function () {
  searchInput.value = "";
  productsFilter.value = {
    logic: "and",
    filters: [{} as FilterDescriptor],
  };
  sortField.value = "";
  sortAscending.value = false;

  // No product should be shown
  // unless a new search is performed
  clearGrid.value = true;
  products.value = [];
  totalProducts.value = 0;
};

const onDetailsClicked = function (product: UkdProductWithSelection) {
  modalProduct.value = product;
  detailsModalVisible.value = true;
};

const onCopyModalClosed = function (cartCount: number) {
  copyIngredientsModalVisible.value = false;
  if (cartCount > -1) {
    selectedIngredientIds.value = [];
    products.value.forEach((p) => (p.selected = false));
  }
};

const onCopyClicked = function () {
  resetPlantSelection();
  copyIngredientsModalVisible.value = true;
};

const onAddClicked = function () {
  ingredientApplicationModalVisible.value = true;
};

const onSearchSubmitted = function (searchValue: string) {
  searchInput.value = searchValue;
  clearGrid.value = false;
  productsSkip.value = 0;
  searchMatchingProductsAsync(productsFilter.value);
};

const resetPlantSelection = function () {
  (props.plants as PlantWithSelection[]).forEach((p) => (p.selected = false));
};

const onProductsChanged = function (options: ItemChangedOptions) {
  if (options.filterEvent) {
    productsFilter.value.filters = options.filterEvent.filter.filters;
  }
  if (productsTake.value != options.take) {
    productsTake.value = options.take;
    GridOptionsService.savePageSizeAsync(tableName, options.take);
  }
  productsSkip.value = options.skip;
  searchMatchingProductsAsync(productsFilter.value);
};

const onSortChanged = function (options: ItemSortChangedOptions) {
  sortField.value = options.field;
  sortAscending.value = options.ascending;
  productsSkip.value = 0;
  searchMatchingProductsAsync(productsFilter.value);
};

const onSelectionChanged = function (
  changedProducts: UkdProductWithSelection[]
) {
  changedProducts.forEach((product) => {
    const index = selectedIngredientIds.value.findIndex(
      (p) => p === product.VueFullUkdId
    );
    if (product.selected) {
      if (index === -1) selectedIngredientIds.value.push(product.VueFullUkdId);
      else
        console.debug(
          `UkdIngredientsTable.vue: onSelectionChanged(): product already selected`,
          product,
          selectedIngredientIds.value[index]
        );
    } else if (index > -1) {
      selectedIngredientIds.value.splice(index, 1);
    } else {
      // If product is deselected and it is not
      // in the selectedIngredientIds array - do nothing
    }
  });
};

const searchMatchingProductsAsync = async function (
  filter: CompositeFilterDescriptor | null = null
) {
  if (productsLoading.value || clearGrid.value) return;

  productsLoading.value = true;

  await handleResponsePromiseAsync(
    getMatchingProductsAsync(
      searchInput.value,
      productsSkip.value,
      productsTake.value,
      filter
    ),
    (data) => {
      products.value = data.ListItems as UkdProductWithSelection[];
      totalProducts.value = data.TotalItemsInList;
    },
    "UkdIngredientsTable.vue",
    "SearchMatchingProducts"
  );

  productsLoading.value = false;
};

const getProductPropValuesAsync = async function (
  column: GridColumnPropsExtended
) {
  let result: UkdProductWithSelection[] = [];
  await handleResponsePromiseAsync(
    getMatchingProductsAsync(
      searchInput.value,
      0,
      999999,
      productsFilter.value,
      column.field
    ),
    (data) => {
      result = data.ListItems as UkdProductWithSelection[];
    },
    "UkdIngredientsTable.vue",
    "getProductPropValuesAsync()"
  );
  return result;
};

const updateColumnMenuIcons = function () {
  setTimeout(() => {
    menuIconHandlerRef.value?.updateColumnMenuIcons();
  }, 100);
};

const resetPagination = function () {
  if (productsTake.value === defaultTake) return;

  productsSkip.value = 0;
  productsTake.value = defaultTake;
  GridOptionsService.clearPageSizeAsync(tableName);
  searchMatchingProductsAsync();
};

const onActionClicked = function (actionType: GridMenuActionType) {
  switch (actionType) {
    case GridMenuActionType.ClearAllFilters:
      clearFilters();
      break;
    case GridMenuActionType.ResetPagination:
      resetPagination();
      break;
    case GridMenuActionType.ResetColumnOrder:
      updateColumnMenuIcons();
      break;
    case GridMenuActionType.ResetAllSettings:
      resetPagination();
      updateColumnMenuIcons();
      break;
  }
  if (gridRef.value) gridRef.value.executeAction(actionType);
};

watchEffect(() => {
  console.debug(
    "UkdIngredientsTable.vue, searchInput watch updated:",
    searchInput.value
  );
  main.UkdProductFilterCriteria = searchInput.value;
});

onBeforeMount(async () => {
  const pageSize = await GridOptionsService.getPageSizeAsync(tableName);
  if (pageSize) productsTake.value = pageSize;
});
</script>

<style lang="scss">
.ukd-ingredients-table {
  .grid-control__container {
    display: flex;
    align-items: center;
  }

  &__actions {
    display: flex;
    gap: 8px;
    align-items: center;
  }

  .pull-right {
    display: flex;
    margin-left: auto;
    gap: 8px;

    .ukd-ingredients-search {
      width: 400px;
    }
  }
}
</style>
